/**
 * stageGestures — touch gestures on the fullscreen canvas (iPad-first).
 *
 * The canvas already doubles as a tap surface (a plain tap sets the tempo). This
 * layers a small pointer recognizer on top that keeps the seam clean: it turns raw
 * pointer events into semantic actions and calls the high-level callbacks main.ts
 * already owns (tempo, panel, visual/palette step, randomize) — it never touches the
 * paramBus directly.
 *
 * Gestures (touch only; a mouse keeps the plain tap-to-tempo behaviour):
 *   • tap                       → onTapTempo   (unchanged; now fires on pointer-up
 *                                                so it can be told apart from the rest)
 *   • long-press (~500ms, still) → onLongPress  (toggle the dock: collapse ↔ reopen)
 *   • 1-finger swipe L/R         → onStepVisual(±1)
 *   • 1-finger swipe U/D         → onStepPalette(±1)
 *   • 2-finger press             → onRandomizeStart / onRandomizeEnd — momentary,
 *     exactly like holding Tab: fires the instant a second finger touches down
 *     (no movement needed) and ends the instant fingers drop back below two. This
 *     mirrors the Random key rather than a one-shot gesture, so main.ts wires it
 *     straight to `keyState.press('Tab', …)` / `release('Tab', …)` — the same seam
 *     dockPointer uses for dock taps — getting Tab's exact momentary behaviour (and
 *     its dock-cell flash) for free instead of duplicating it here.
 *
 * The single-finger gesture *decision* is a pure function (`classifyGesture`) so it
 * can be unit tested without a DOM; `attachStageGestures` is the thin event-plumbing
 * wrapper, including the immediate (not swipe-gated) 2-finger press/release.
 */

/** Movement under this (px) still counts as "held in place" (tap / long-press). */
export const MOVE_SLOP_PX = 10;
/** A swipe must travel at least this far (px) on its dominant axis. */
export const SWIPE_MIN_PX = 45;
/** A tap must complete within this long (ms); longer + still = a long-press. */
export const TAP_MAX_MS = 400;
/** Hold this long (ms) without moving to trigger the long-press. */
export const LONG_PRESS_MS = 500;

export type GestureAction =
  | { readonly type: 'tap' }
  | { readonly type: 'swipe-visual'; readonly dir: 1 | -1 }
  | { readonly type: 'swipe-palette'; readonly dir: 1 | -1 }
  | null;

export interface GestureSample {
  /** Net displacement of the primary (single) pointer, px. */
  readonly dx: number;
  readonly dy: number;
  /** Interaction duration (ms). */
  readonly dtMs: number;
  /** Whether the long-press already fired mid-gesture (then the up is a no-op). */
  readonly longPressFired: boolean;
}

/**
 * Decide what a finished single-finger pointer interaction was. Long-press is
 * handled separately (by a timer, mid-gesture), so a `longPressFired` sample
 * resolves to `null` here. The 2-finger press/release is decided directly in
 * `attachStageGestures` (it's momentary, not a shape to classify on release).
 */
export function classifyGesture(s: GestureSample): GestureAction {
  if (s.longPressFired) return null;

  const absX = Math.abs(s.dx);
  const absY = Math.abs(s.dy);
  const dist = Math.hypot(s.dx, s.dy);

  if (dist >= SWIPE_MIN_PX) {
    if (absX >= absY) {
      // Swipe left (dx<0) → previous visual; swipe right → next.
      return { type: 'swipe-visual', dir: s.dx < 0 ? -1 : 1 };
    }
    // Swipe up (dy<0) → next palette; swipe down → previous.
    return { type: 'swipe-palette', dir: s.dy < 0 ? 1 : -1 };
  }
  if (dist < MOVE_SLOP_PX && s.dtMs <= TAP_MAX_MS) return { type: 'tap' };
  return null; // an ambiguous small drag — ignore
}

export interface StageGestureDeps {
  readonly stage: HTMLElement;
  readonly onTapTempo: () => void;
  readonly onLongPress: () => void;
  readonly onStepVisual: (dir: 1 | -1) => void;
  readonly onStepPalette: (dir: 1 | -1) => void;
  /** A second finger touches down — start the momentary randomize (Tab keydown). */
  readonly onRandomizeStart: () => void;
  /** Fingers drop back below two — end it (Tab keyup). */
  readonly onRandomizeEnd: () => void;
  /** Injectable clock for tests; defaults to performance.now. */
  readonly now?: () => number;
}

/** Bind the recognizer to the stage. Returns a detach function. */
export function attachStageGestures(deps: StageGestureDeps): () => void {
  const { stage, onTapTempo, onLongPress, onStepVisual, onStepPalette, onRandomizeStart, onRandomizeEnd } = deps;
  const now = deps.now ?? (() => performance.now());

  const active = new Set<number>();
  let primaryId = -1;
  let startX = 0;
  let startY = 0;
  let startT = 0;
  let lastX = 0;
  let lastY = 0;
  let isTouch = false;
  let moved = false;
  let longPressFired = false;
  let randomizeActive = false;
  /** Set once a second finger joins, so releasing the rest never also fires a tap/swipe. */
  let multiTouchUsed = false;
  let lpTimer: ReturnType<typeof setTimeout> | undefined;

  const clearLp = (): void => {
    if (lpTimer !== undefined) {
      clearTimeout(lpTimer);
      lpTimer = undefined;
    }
  };

  const reset = (): void => {
    active.clear();
    primaryId = -1;
    moved = false;
    longPressFired = false;
    randomizeActive = false;
    multiTouchUsed = false;
    clearLp();
  };

  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    const wasEmpty = active.size === 0;
    active.add(e.pointerId);

    if (wasEmpty) {
      // Fresh interaction begins with this pointer.
      primaryId = e.pointerId;
      startX = lastX = e.clientX;
      startY = lastY = e.clientY;
      startT = now();
      moved = false;
      longPressFired = false;
      isTouch = e.pointerType !== 'mouse';
      clearLp();
      if (isTouch) {
        lpTimer = setTimeout(() => {
          lpTimer = undefined;
          if (active.size === 1 && !moved) {
            longPressFired = true;
            onLongPress();
          }
        }, LONG_PRESS_MS);
      }
      return;
    }

    // A second (or later) finger joins an in-progress touch: rules out tap /
    // long-press for the first finger, and — the instant it reaches two —
    // starts the momentary randomize, exactly like a Tab keydown.
    clearLp();
    if (isTouch) {
      multiTouchUsed = true;
      if (active.size >= 2 && !randomizeActive) {
        randomizeActive = true;
        onRandomizeStart();
      }
    }
  };

  const onMove = (e: PointerEvent): void => {
    if (!active.has(e.pointerId)) return;
    if (e.pointerId === primaryId) {
      lastX = e.clientX;
      lastY = e.clientY;
    }
    if (Math.hypot(e.clientX - startX, e.clientY - startY) > MOVE_SLOP_PX) {
      moved = true;
      clearLp();
    }
  };

  const finish = (e: PointerEvent): void => {
    if (!active.delete(e.pointerId)) return;
    if (e.pointerId === primaryId) {
      lastX = e.clientX;
      lastY = e.clientY;
    }

    // Fingers dropped back below two: end the momentary randomize immediately,
    // same as a Tab keyup — regardless of whether any finger is still down.
    if (randomizeActive && active.size < 2) {
      randomizeActive = false;
      onRandomizeEnd();
    }

    if (active.size > 0) return; // wait for every finger to lift

    const dx = lastX - startX;
    const dy = lastY - startY;
    const dtMs = now() - startT;
    const lpFired = longPressFired;
    const wasMulti = multiTouchUsed;
    const touch = isTouch;
    reset();

    if (lpFired || wasMulti) return; // already handled (long-press timer / randomize)

    if (!touch) {
      // Mouse: tap-to-tempo only, no swipe / long-press / multi-touch.
      if (dtMs <= TAP_MAX_MS && Math.hypot(dx, dy) < MOVE_SLOP_PX) onTapTempo();
      return;
    }

    const action = classifyGesture({ dx, dy, dtMs, longPressFired: false });
    if (!action) return;
    switch (action.type) {
      case 'tap': onTapTempo(); break;
      case 'swipe-visual': onStepVisual(action.dir); break;
      case 'swipe-palette': onStepPalette(action.dir); break;
    }
  };

  stage.addEventListener('pointerdown', onDown);
  stage.addEventListener('pointermove', onMove);
  stage.addEventListener('pointerup', finish);
  stage.addEventListener('pointercancel', finish);

  return () => {
    clearLp();
    stage.removeEventListener('pointerdown', onDown);
    stage.removeEventListener('pointermove', onMove);
    stage.removeEventListener('pointerup', finish);
    stage.removeEventListener('pointercancel', finish);
  };
}
